import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import applicationService, {
  Application,
} from "../services/api/applicationService";

const PaymentCompletion: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);

  // PayFast callback parameters
  const basketId =
    searchParams.get("BASKET_ID") || searchParams.get("basket_id");
  const transactionId =
    searchParams.get("TRANSACTION_ID") || searchParams.get("transaction_id");
  const responseCode =
    searchParams.get("RESPONSE_CODE") || searchParams.get("err_code");
  const applicationId = searchParams.get("applicationId");
  const paymentId = searchParams.get("payment_id"); // For legacy webhook redirects

  useEffect(() => {
    if (applicationId) {
      // If we have PayFast callback data, send it to backend first
      if (basketId && transactionId) {
        handlePayFastCallback();
      } else {
        checkPaymentStatus();
      }
    } else if (paymentId) {
      // Try to find application by payment ID
      findApplicationByPaymentId();
    } else {
      setError("Invalid payment completion link. Please contact support.");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, paymentId, basketId, transactionId]);

  const findApplicationByPaymentId = async () => {
    try {
      // This would require a new API endpoint to find application by payment ID
      // For now, show a simplified success message
      setLoading(false);
      setPaymentVerified(true);
      setApplication({
        applicationNumber: `PAYMENT-${paymentId}`,
        paymentStatus: "COMPLETED",
        paymentReference: paymentId,
      } as any);
    } catch (err: any) {
      console.error("Error finding application by payment ID:", err);
      setError(
        "Payment completed, but unable to load application details. Please contact support."
      );
      setLoading(false);
    }
  };

  const handlePayFastCallback = async () => {
    try {
      // Send callback data to backend
      await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000"
        }/api/gopayfast/callback/success`,
        {
          BASKET_ID: basketId,
          TRANSACTION_ID: transactionId,
          TRANSACTION_STATUS:
            searchParams.get("TRANSACTION_STATUS") ||
            searchParams.get("transaction_status"),
          RESPONSE_CODE: responseCode,
          RESPONSE_MESSAGE:
            searchParams.get("RESPONSE_MESSAGE") || searchParams.get("err_msg"),
          TXNAMT:
            searchParams.get("TXNAMT") ||
            searchParams.get("transaction_amount"),
          ORDER_DATE:
            searchParams.get("ORDER_DATE") || searchParams.get("order_date"),
        }
      );

      // Then check payment status
      await checkPaymentStatus();
    } catch (err: any) {
      console.error("Callback error:", err);
      // Still try to check payment status
      await checkPaymentStatus();
    }
  };

  const checkPaymentStatus = async () => {
    try {
      // Get application details
      const application = await applicationService.getApplicationById(
        applicationId!
      );
      setApplication(application);

      // Verify payment is completed
      if (application.paymentStatus === "COMPLETED") {
        setPaymentVerified(true);
      } else {
        setError(
          "Payment verification in progress. Please wait a moment and refresh the page."
        );
      }
    } catch (err: any) {
      console.error("Check payment status error:", err);
      setError(
        "Failed to verify payment status. Please contact support if this issue persists."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!applicationId) {
      setError("Missing application information");
      return;
    }

    if (application?.paymentStatus !== "COMPLETED") {
      setError("Payment must be completed before finalizing application");
      return;
    }

    // Payment webhook already transitions APPROVED → SUBMITTED
    // Just show success and redirect
    setIsSubmittingApplication(true);

    // Create a success overlay
    const successOverlay = document.createElement("div");
    successOverlay.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; align-items: center; justify-content: center;">
        <div style="background: white; padding: 2rem; border-radius: 1rem; text-align: center; max-width: 400px; margin: 1rem;">
          <div style="width: 4rem; height: 4rem; background: #10B981; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem;">
            <svg style="width: 2rem; height: 2rem; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.5rem; color: #111827;">Payment Complete!</h2>
          <p style="color: #6B7280; margin-bottom: 1.5rem;">Your payment has been confirmed and your application has been finalized. You'll be redirected to track your application.</p>
          <div style="background: #F3F4F6; padding: 1rem; border-radius: 0.5rem; font-size: 0.875rem; color: #4B5563;">
            <strong>Application Number:</strong> ${application?.applicationNumber || "N/A"}
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(successOverlay);

    // Redirect after showing success message
    setTimeout(() => {
      navigate(
        "/my-applications"
      );
    }, 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-cyan-200 border-t-cyan-600 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-cyan-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <h2 className="mt-6 text-xl font-semibold text-gray-900">
            Verifying Your Payment
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we confirm your transaction...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
              <svg
                className="h-10 w-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Payment Verification Issue
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={checkPaymentStatus}
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() =>
                  (window.location.href = "mailto:support@raahglobal.com")
                }
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-cyan-50 to-blue-50">
      {/* Header with RAAH Global branding */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img
                className="h-8 w-auto"
                src="/logo.jpg"
                alt="RAAH Global"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                }}
              />
              <span className="ml-3 text-xl font-bold text-gray-900">
                RAAH Global
              </span>
            </div>
            <div className="text-sm text-gray-500">
              University Admissions Platform
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full">
          {/* Success Message */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-gradient-to-r from-green-400 to-green-600 mb-6">
                <svg
                  className="h-12 w-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                Payment Successful!
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Thank you for choosing RAAH Global for your university admission
                journey.
              </p>

              {paymentVerified && (
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-6">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4"
                    />
                  </svg>
                  Payment Verified
                </div>
              )}
            </div>
          </div>

          {/* Application Details */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-cyan-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Application Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Application Number
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {application?.applicationNumber}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Course
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {application?.course?.title}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    University
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {application?.course?.university?.name}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Payment Status
                  </label>
                  <div className="flex items-center mt-1">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${application?.paymentStatus === "COMPLETED"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                        }`}
                    >
                      {application?.paymentStatus === "COMPLETED" ? (
                        <>
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Completed
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4 mr-1 animate-spin"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Processing
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {application?.paymentReference && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Transaction ID
                    </label>
                    <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded mt-1">
                      {application.paymentReference}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-gray-600 block mb-1">
                    Amount Paid
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    PKR 45,000
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-cyan-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Next Steps
            </h2>

            {application?.paymentStatus === "COMPLETED" ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg
                      className="w-5 h-5 text-cyan-600 mr-3 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <h3 className="font-semibold text-cyan-900 mb-1">
                        Ready to Submit Your Application
                      </h3>
                      <p className="text-sm text-cyan-800">
                        Your payment has been verified successfully. Click the
                        button below to officially submit your application for
                        review. Our admission experts will review your documents
                        and get back to you within 2-3 business days.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleSubmitApplication}
                    disabled={isSubmittingApplication}
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 font-semibold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmittingApplication ? (
                      <>
                        <svg
                          className="w-5 h-5 mr-2 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        Submitting Application...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                          />
                        </svg>
                        Submit Application
                      </>
                    )}
                  </button>

                  <button
                    onClick={() =>
                      navigate("/signin?message=payment-completed")
                    }
                    className="flex-1 px-6 py-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                      />
                    </svg>
                    Sign In to View Applications
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-1">
                      Payment Processing
                    </h3>
                    <p className="text-sm text-yellow-800">
                      Your payment is being verified. This usually takes a few
                      moments. Please refresh the page or check back shortly.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Support Information */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Need help? Our support team is here for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@raahglobal.com"
                className="inline-flex items-center px-4 py-2 text-cyan-600 hover:text-cyan-700 transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                support@raahglobal.com
              </a>
              <a
                href="tel:+1234567890"
                className="inline-flex items-center px-4 py-2 text-cyan-600 hover:text-cyan-700 transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                +1 (234) 567-890
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCompletion;
